// src/layout/AppShell.js
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import "./Layout.css";
import Search from "../pages/Search";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
//import directvLogo from "../assets/DIRECTV Logo.jpg";
import directvLogo from "../assets/DTV_Logo_Ozone_RGB.png";


const OPEN_FLAG = "npw.search.reopen";
const CACHE_KEY = "npw.search.cache.v1";

/**
 * Normalize a path so detail pages highlight their parent in the sidebar.
 *  - /approvals/123  -> /approvals
 *  - /manage-requests/456 -> /manage-requests
 *  - /onboarding/new/789 -> /onboarding/new
 *  - others unchanged
 */
function normalizePathForNav(pathname = "") {
    const p = String(pathname || "");
    if (p.startsWith("/approvals/")) return "/approvals";
    if (p.startsWith("/manage-requests/")) return "/manage-requests";
    if (p.startsWith("/onboarding/new/")) return "/onboarding/new";
    const generic = p.replace(/\/[A-Za-z0-9-]{4,}$/, "");
    return generic || p;
}

/**
 * Props:
 *  - activeKey?: string (optional override for menu highlighting)
 *  - publicMode?: boolean (Header hides search/actions on true)
 *  - noSidebar?: boolean  (when true, hides the left sidebar)
 */
export default function AppShell({ children, activeKey, publicMode = false, noSidebar = false }) {
    const location = useLocation();
    const nav = useNavigate();
    const currentRole = (sessionStorage.getItem("tb_role") || "").trim();
    const hideMainSearch = currentRole.toLowerCase() === "read only";

    // ---- Search overlay state ----
    const [searchTrigger, setSearchTrigger] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");

    // ---- Logo fallback ----
    const [logoOk, setLogoOk] = useState(true);

    // ---- Left menu collapse (global focus mode) ----
    const [menuCollapsed, setMenuCollapsed] = useState(() => {
        try {
            return sessionStorage.getItem("tb_focus_mode") === "true";
        } catch {
            return false;
        }
    });

    const handleBack = () => setSearchTrigger(0);
    const handleTrigger = () => setSearchTrigger((c) => c + 1);
    const handleToggleMenu = () => {
        /* If we're in unauthorized view, redirect to dashboard on menu toggle */
        if (location?.pathname?.includes('/unauthorized')) {
            nav("/dashboard", { replace: true });
        }
        setMenuCollapsed((v) => !v);
    };

    // Reopen search overlay if flagged (Return from RequestDetail, etc.)
    useEffect(() => {
        if (publicMode || hideMainSearch) return; // 🔒 Never auto-open search in public or Read Only views
        if (sessionStorage.getItem(OPEN_FLAG) === "1") {
            sessionStorage.removeItem(OPEN_FLAG);
            try {
                const raw = sessionStorage.getItem(CACHE_KEY);
                if (raw) {
                    const cache = JSON.parse(raw);
                    if (cache?.term) setSearchTerm(cache.term);
                }
            } catch { /* ignore */ }
            setSearchTrigger((c) => (c > 0 ? c + 1 : 1));
        }
    }, [location.key, publicMode, hideMainSearch]);

    // Persist + apply focus mode at app shell level
    useEffect(() => {
        try { sessionStorage.setItem("tb_focus_mode", String(!!menuCollapsed)); } catch { /* ignore */ }
        try {
            const root = document.documentElement;
            root.classList.toggle("tb-focus-mode", !!menuCollapsed);
            return () => root.classList.remove("tb-focus-mode");
        } catch {
            return undefined;
        }
    }, [menuCollapsed]);

    // Keyboard shortcuts (disabled in publicMode):
    //  - "/" opens search (unless focused in an input/textarea or search already open)
    //  - "Escape" closes search if open
    const searchOpenRef = useRef(false);
    searchOpenRef.current = searchTrigger > 0;

    useEffect(() => {
        if (publicMode || hideMainSearch) return; // 🔒 No hotkeys in public/unauthorized or Read Only mode

        function isEditableTarget(el) {
            const tag = el?.tagName?.toLowerCase();
            if (!tag) return false;
            if (tag === "input" || tag === "textarea") return true;
            if (el?.isContentEditable) return true;
            return false;
        }
        const onKey = (e) => {
            const t = e.target;
            const inField = isEditableTarget(t);

            if (e.key === "/" && !inField && !searchOpenRef.current) {
                e.preventDefault();
                setSearchTrigger((c) => (c > 0 ? c + 1 : 1));
                return;
            }
            if (e.key === "Escape" && searchOpenRef.current) {
                e.preventDefault();
                setSearchTrigger(0);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [publicMode, hideMainSearch]);

    // Resolve active route key for sidebar highlighting
    const resolvedActiveKey = useMemo(() => {
        if (activeKey && typeof activeKey === "string" && activeKey.trim()) return activeKey;
        return normalizePathForNav(location.pathname);
    }, [activeKey, location.pathname]);

    return (
        <div className="tb-app-bg">
            {/* Global toast container (avoid duplicates via containerId) */}
            <ToastContainer containerId="global" position="top-right" autoClose={3000} newestOnTop />
            <div className="tb-app-frame">
                <div className="tb-app-surface">
                    <div className={`tb-grid ${noSidebar ? "no-sidebar" : ""} ${(!noSidebar && menuCollapsed) ? "menu-collapsed" : ""}`}>
                        {!noSidebar && (
                            <aside className={`tb-left ${menuCollapsed ? "tb-left-collapsed" : ""}`}>
                                <div className="tb-left-logo">
                                    {logoOk ? (
                                        <img
                                            src={directvLogo}
                                            alt="DIRECTV"
                                            className="dtv-logo-img"
                                            draggable="false"
                                            onError={() => setLogoOk(false)}
                                            decoding="async"
                                            loading="eager"
                                        />
                                    ) : (
                                        <div className="dtv-logo-fallback" aria-label="DIRECTV">
                                            DIRECTV
                                        </div>
                                    )}
                                </div>
                                <Sidebar activeKey={resolvedActiveKey} collapsed={menuCollapsed} />
                            </aside>
                        )}

                        <main className="tb-main">
                            <Header
                                publicMode={publicMode}
                                hideSearch={hideMainSearch}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                setSearchTrigger={handleTrigger}
                                menuCollapsed={menuCollapsed}
                                onToggleMenu={handleToggleMenu}
                            />

                            {/* In publicMode/ReadOnly mode, never render the search overlay */}
                            {!publicMode && !hideMainSearch && searchTrigger > 0 ? (
                                <Search searchTerm={searchTerm} trigger={searchTrigger} onClose={handleBack} />
                            ) : (
                                <div className="tb-main-body">{children}</div>
                            )}

                            <Footer />
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
}
